'use client'

import React, { useEffect, useState } from 'react'
import { companyNewsData } from '@/services/companyDetailsService'
import { CompanyNewsResponse } from '@/app/types/companyNewsType'
import { useTranslation } from '@/app/i18n/client'
import CustomErrorBoundary from '@/app/[lng]/ErrorBoundary'
import { NewsDetail } from '@/app/types/newsType'
import NewsCardCompany from '@/app/[lng]/components/(MiniWebsite)/news/NewsCardCompany'
import { Skeleton } from '@/components/ui/skeleton'
import CompanyBlocker from '@/app/[lng]/components/BlockerModal/CompanyBlocker'
import SelectDropDown from '@/components/selectDropDown'
import { useParams } from 'next/navigation'
import LimitPagination from '@/components/LimitPagination'
import Image from 'next/image'
import { RootState } from '@/app/globalRedux/store'
import { useSelector } from 'react-redux'

const News: React.FC<{ id: string }> = ({ id }) => {
  const { t } = useTranslation()
  const params = useParams()
  const [showContent, setShowContent] = useState<boolean | null>(null)
  const [news, setNews] = useState<NewsDetail[]>([])
  const [totalCount, setTotalCount] = useState<number>(1)
  const [page, setPage] = useState<number>(1)
  const [order, setOrder] = useState<string>('desc')
  const { access_count, isBeneficiary, accessPer } = useSelector(
    (state: RootState) => state.user
  )
  const fetchData = async () => {
    try {
      const newsResponse: CompanyNewsResponse = await companyNewsData(
        id,
        page,
        params?.lng ?? 'en',
        order
      )
      if (newsResponse?.message === 'Access Exhausted') {
        setShowContent(false)
      } else {
        setShowContent(true)
        setNews(newsResponse.news)
        setTotalCount(newsResponse.count)
      }
    } catch (error) {
      console.error('Error fetching offers:', error)
    }
  }

  useEffect(() => {
    fetchData()
  }, [page, order])
  if (showContent === null) {
    return <Skeleton className="w-[952px] h-[500px]" />
  }

  return (
    <div className="flex">
      {!showContent && (
        <CompanyBlocker
          access_count={access_count}
          isBeneficiary={isBeneficiary}
          accessPer={accessPer}
        />
      )}
      <div className="w-[952px]">
        <div className="flex justify-between border-b border-solid border-gray_btn pb-[17px] mb-[19.5px]">
          <h1 className="flex items-center">{t('News')}</h1>
          <div className="w-fit">
            <SelectDropDown key={0} order={order} setOrder={setOrder} />
          </div>
        </div>
        {news &&
          news?.map((newsItem: NewsDetail) => (
            <CustomErrorBoundary key={newsItem.id}>
              <div key={newsItem.id}>
                <NewsCardCompany newsData={newsItem} />
              </div>
            </CustomErrorBoundary>
          ))}
        {totalCount > 0 ? (
          <div className=" px-1">
            <LimitPagination
              currentPage={page}
              pageCount={Math.ceil(totalCount / 5)}
              visiblePages={5}
              onPageChange={setPage}
            />
          </div>
        ) : (
          news &&
          news?.length === 0 &&
          totalCount === 0 && (
            <div className="p-16 flex flex-col gap-2 items-center justify-center text-primary-foreground">
              <Image
                loading="lazy"
                width={86}
                height={94}
                src="/assets/Icons/noImage/noNewsIcon.svg"
                alt=""
                className="w-[95px] h-[94px] object-cover"
              />
              <p>{t('News not available at this moment')}</p>
            </div>
          )
        )}
      </div>
    </div>
  )
}

export default News
